<template>
  <div class="code-input" :class="{ compact }">
    <input
      v-for="(_, index) in length"
      :key="index"
      ref="digitRefs"
      class="digit"
      :class="{ filled: Boolean(boxes[index]) }"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      :disabled="disabled"
      :value="boxes[index]"
      @input="onInput(index, $event)"
      @keydown="onKeydown(index, $event)"
      @paste.prevent="onPaste($event)"
      @focus="($event.target as HTMLInputElement).select()"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    length?: number;
    disabled?: boolean;
    /** Slightly smaller boxes for tighter modals (the 2FA setup one) */
    compact?: boolean;
  }>(),
  { length: 6, disabled: false, compact: false },
);

const model = defineModel<string>({ default: '' });
const emit = defineEmits<{ complete: [code: string] }>();

const digitRefs = ref<HTMLInputElement[]>([]);
// Positional: each box owns one slot, so clearing a middle box leaves a gap instead of
// shifting the tail left. model stays the join of filled slots (short = incomplete).
const boxes = ref<string[]>(Array.from({ length: props.length }, () => ''));

// An external model set (e.g. the parent clearing it after a wrong code) reseeds left-aligned
watch(
  model,
  (value) => {
    if (value === boxes.value.join('')) return;
    const chars = (value || '').replace(/\D/g, '').slice(0, props.length).split('');
    boxes.value = Array.from({ length: props.length }, (_, index) => chars[index] ?? '');
  },
  { immediate: true },
);

function commit() {
  const code = boxes.value.join('');
  model.value = code;
  if (code.length === props.length) {
    emit('complete', code);
  }
}

// A box accepts however many digits land in it (fast typing outruns the focus hand-off)
// and distributes them forward from its position
function onInput(index: number, event: Event) {
  const input = event.target as HTMLInputElement;
  const typedDigits = input.value.replace(/\D/g, '');

  if (!typedDigits) {
    boxes.value[index] = '';
    input.value = '';
    commit();
    return;
  }

  for (let offset = 0; offset < typedDigits.length && index + offset < props.length; offset++) {
    boxes.value[index + offset] = typedDigits[offset];
  }
  input.value = boxes.value[index];

  const nextIndex = Math.min(index + typedDigits.length, props.length - 1);
  digitRefs.value[nextIndex]?.focus();
  commit();
}

function onKeydown(index: number, event: KeyboardEvent) {
  if (event.key === 'Backspace' && !boxes.value[index] && index > 0) {
    digitRefs.value[index - 1]?.focus();
  }
}

// A code pasted anywhere fills the row from the start
function onPaste(event: ClipboardEvent) {
  const chars = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, props.length).split('');
  boxes.value = Array.from({ length: props.length }, (_, index) => chars[index] ?? '');
  digitRefs.value[Math.min(chars.length, props.length - 1)]?.focus();
  commit();
}

function focus() {
  digitRefs.value[0]?.focus();
}

defineExpose({ focus });
</script>

<style scoped lang="scss">
.code-input {
  display: flex;
  gap: var(--t-spacing-sm);
}

.digit {
  width: 3rem;
  height: 3.5rem;
  border: 1px solid var(--t-color-neutral-300);
  border-radius: var(--t-border-radius-sm);
  background: var(--t-color-neutral-white);
  text-align: center;
  font: var(--t-font-weight-semi-bold) 1.5rem var(--t-font-family-primary);
  color: var(--t-text-color-primary);
  outline: none;
  transition: border-color 150ms ease;

  &.filled {
    border-color: var(--t-color-neutral-950);
  }

  &:focus {
    border: 2px solid var(--t-color-neutral-950);
  }

  &:disabled {
    background: var(--t-color-neutral-50);
  }
}

.compact .digit {
  width: 2.75rem;
  height: 3.25rem;
  font-size: 1.25rem;
}
</style>
